/**
 * Query Key factory for all dashboard queries.
 *
 * @module Dashboard
 */

import { DashboardQueryProps } from "@customTypes/dashboard";


export const dashboardQKeys = {
  all: () => ["dashboard"] as const,

  doctorProfileActivity: (numDays: number, platform = "website") =>
    [
      ...dashboardQKeys.all(),
      "doctorProfileActivity",
      numDays,
      platform,
    ] as const,

  consultationsTrend: () =>
    [...dashboardQKeys.all(), "consultationsTrends"] as const,

  consultationsTrendWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.consultationsTrend(), { ...filters }] as const,

  consultationActivity: () =>
    [...dashboardQKeys.all(), "consultationActivity"] as const,

  consultationActivityWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.consultationActivity(), { ...filters }] as const,

  analysisActivity: () =>
    [...dashboardQKeys.all(), "analysisActivity"] as const,

  analysisActivityWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.analysisActivity(), { ...filters }] as const,

  prescriptionActivity: () =>
    [...dashboardQKeys.all(), "prescriptionActivity"] as const,

  prescriptionActivityWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.prescriptionActivity(), { ...filters }] as const,

  frequentServices: () =>
    [...dashboardQKeys.all(), "frequentServices"] as const,

  frequentServicesWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.frequentServices(), { ...filters }] as const,

  labsTrend: () => [...dashboardQKeys.all(), "labsTrend"] as const,

  labsTrendWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.labsTrend(), { ...filters }] as const,

  prescriptionsTrend: () =>
    [...dashboardQKeys.all(), "prescriptionsTrend"] as const,

  prescriptionsTrendWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.prescriptionsTrend(), { ...filters }] as const,

  staffSessions: () => [...dashboardQKeys.all(), "staffSessions"] as const,

  staffSessionsWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.staffSessions(), { ...filters }] as const,

  singleStaffSession: () =>
    [...dashboardQKeys.all(), "singleStaffSession"] as const,

  singleStaffSessionWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.singleStaffSession(), { ...filters }] as const,

  staffEngagementTrend: () =>
    [...dashboardQKeys.all(), "staffEngagementTrend"] as const,

  staffEngagementTrendWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.staffEngagementTrend(), { ...filters }] as const,

  staffPerformance: () =>
    [...dashboardQKeys.all(), "staffPerformance"] as const,

  staffPerformanceWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.staffPerformance(), { ...filters }] as const,

  topPerformingStaff: () =>
    [...dashboardQKeys.all(), "topPerformingStaff"] as const,

  topPerformingStaffWithFilters: (
    filters: DashboardQueryProps & { staffType?: string; recordType?: string }
  ) => [...dashboardQKeys.topPerformingStaff(), { ...filters }] as const,

  totalAppointmentStatuses: () =>
    [...dashboardQKeys.all(), "totalAppointmentStatuses"] as const,

  totalAppointmentStatusesWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalAppointmentStatuses(), { ...filters }] as const,

  totalBillsStatuses: () =>
    [...dashboardQKeys.all(), "totalBillsStatuses"] as const,

  totalBillsStatusesWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalBillsStatuses(), { ...filters }] as const,

  totalConsultations: () =>
    [...dashboardQKeys.all(), "totalConsultations"] as const,

  totalConsultationsWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalConsultations(), { ...filters }] as const,

  totalConsultationStatuses: () =>
    [...dashboardQKeys.all(), "totalConsultationStatuses"] as const,

  totalConsultationStatusesWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalConsultationStatuses(), { ...filters }] as const,

  totalEarnings: () => [...dashboardQKeys.all(), "totalEarnings"] as const,

  totalEarningsWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalEarnings(), { ...filters }] as const,

  totalLabStatuses: () =>
    [...dashboardQKeys.all(), "totalLabStatuses"] as const,

  totalLabStatusesWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalLabStatuses(), { ...filters }] as const,

  totalPatients: () => [...dashboardQKeys.all(), "totalPatients"] as const,

  totalPatientsWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalPatients(), { ...filters }] as const,

  totalPrescriptions: () =>
    [...dashboardQKeys.all(), "totalPrescriptions&Medications"] as const,

  totalPrescriptionsWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalPrescriptions(), { ...filters }] as const,

  totalUsersByRole: () =>
    [...dashboardQKeys.all(), "totalUsersByRole"] as const,

  totalUsersByRoleWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.totalUsersByRole(), { ...filters }] as const,

  payoutSummary: () => [...dashboardQKeys.all(), "payoutSummary"],

  payoutSummaryWithFilters: (filters: DashboardQueryProps) => [
    ...dashboardQKeys.payoutSummary(),
    { ...filters },
  ],

  earningsDailyTrend: () =>
    [...dashboardQKeys.all(), "earningsDailyTrend"] as const,

  earningsDailyTrendWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.earningsDailyTrend(), { ...filters }] as const,

  earningsByStaff: () => [...dashboardQKeys.all(), "earningsByStaff"] as const,

  earningsByStaffWithFilters: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.earningsByStaff(), { ...filters }] as const,

  earningsByServices: () =>
    [...dashboardQKeys.all(), "earningsByServices"] as const,

  earningsByServicesWithFilters: (
    filters: DashboardQueryProps & {
      billStatus?: string;
      reportStatus?: string;
      payoutStatus?: string;
    }
  ) => [...dashboardQKeys.earningsByServices(), { ...filters }] as const,

  payouts: () => [...dashboardQKeys.all(), "payouts"] as const,

  payoutIssuePreview: (filters: DashboardQueryProps) =>
    [...dashboardQKeys.all(), "payoutIssuePreview", { ...filters }] as const,

  payoutsWithFilters: (
    filters: any
  ) => [...dashboardQKeys.payouts(), { ...filters }] as const,
};
